import React, { memo } from "react";

import { Contact } from "services/api/contacts/types";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";

import { WAREHOUSE_INTRO_SAMPLE } from "../consts";
import { WarehouseSceneProps } from "../types";

const getContactName = (contact: Contact) =>
  [contact.firstName, contact.lastName].filter(Boolean).join(" ") || contact.name || contact.companyName || "";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

/** Korak "Kontakti": osobe zadužene za skladište, poziv i tko se javlja. */
const WarehouseContactsScene: React.FC<WarehouseSceneProps> = ({ formValues }) => {
  const contacts =
    formValues.contacts.length > 0
      ? formValues.contacts.map((contact) => {
          const name = getContactName(contact);
          return { name, role: contact.jobTitle || contact.companyName || "Kontakt skladišta", initials: getInitials(name) };
        })
      : WAREHOUSE_INTRO_SAMPLE.contacts;
  const firstContact = formValues.contacts[0];
  const phone = firstContact?.primaryContactPhone?.phoneNumber ?? "+385 91 234 5678";

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Kontakti skladišta</div>
          <div className="text-sm font-bold text-slate-800 truncate">{formValues.name || WAREHOUSE_INTRO_SAMPLE.name}</div>
          <div className="mt-3 space-y-2">
            {contacts.slice(0, 3).map((contact) => (
              <div key={contact.name} className="flex items-center gap-3 rounded-md bg-slate-50 px-2 py-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-[0.65rem] font-bold text-blue-700 flex-shrink-0">
                  {contact.initials}
                </span>
                <div className="min-w-0">
                  <div className="text-[0.7rem] font-semibold text-slate-700 truncate">{contact.name}</div>
                  <div className="text-[0.6rem] text-slate-400 truncate">{contact.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Poziv</div>
          <div className="mt-2 text-xs font-bold text-slate-800">{contacts[0]?.name}</div>
          <div className="text-[0.65rem] text-slate-400">{phone}</div>
          <div className="mt-3 flex gap-2">
            <span className="flex-1 rounded-sm bg-emerald-500 py-1 text-center text-[0.6rem] font-semibold text-white">Nazovi</span>
            <span className="flex-1 rounded-sm bg-slate-100 py-1 text-center text-[0.6rem] font-semibold text-slate-600">Email</span>
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Znate koga nazvati"
          subtitle="Kontakti uz svako skladište"
          iconClassName="bg-blue-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(WarehouseContactsScene);
